import { adToBs, getDaysInBSMonth, isValidBSDate } from '../utils/converter';

type BSDate = { year: number; month: number; day: number };

function resolveBSDate(date: Date | string | number | BSDate): BSDate {
  if (date instanceof Date) {
    return adToBs(date);
  }
  if (typeof date === 'number') {
    return adToBs(date);
  }
  if (typeof date === 'string') {
    const parts = date.split(/[-/.]/);
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      if (isValidBSDate(year, month, day)) {
        return { year, month, day };
      }
    }
    const adDate = new Date(date);
    if (!isNaN(adDate.getTime())) {
      return adToBs(adDate);
    }
    throw new Error(`Invalid date input: "${date}"`);
  }
  if (typeof date === 'object' && date !== null && 'year' in date && 'month' in date && 'day' in date) {
    if (!isValidBSDate(date.year, date.month, date.day)) {
      throw new Error(`Invalid BS date: ${date.year}-${date.month}-${date.day}`);
    }
    return date;
  }
  throw new Error('Invalid date type');
}

/**
 * Calculates the age in years, months, and days between a Date of Birth (dob)
 * and a reference date (defaults to the current local date in Nepal) within the BS calendar.
 */
export function calculateAgeBS(
  dobInput: Date | string | number | BSDate,
  refInput: Date | string | number | BSDate = new Date()
): { years: number; months: number; days: number } {
  const dob = resolveBSDate(dobInput);
  const ref = resolveBSDate(refInput);

  // Check if dob is after ref
  const dobVal = dob.year * 10000 + dob.month * 100 + dob.day;
  const refVal = ref.year * 10000 + ref.month * 100 + ref.day;
  if (dobVal > refVal) {
    throw new Error('Date of birth cannot be after the reference date.');
  }

  let years = ref.year - dob.year;
  let months = ref.month - dob.month;
  let days = ref.day - dob.day;

  // Borrow days
  if (days < 0) {
    // Determine the previous month of the reference date
    let prevMonth = ref.month - 1;
    let prevYear = ref.year;
    if (prevMonth === 0) {
      prevMonth = 12;
      prevYear = ref.year - 1;
    }
    
    const prevMonthDays = getDaysInBSMonth(prevYear, prevMonth);
    days += prevMonthDays;
    months -= 1;
  }

  // Borrow months
  if (months < 0) {
    months += 12;
    years -= 1;
  }

  return { years, months, days };
}
